import React from 'react';
import {
    ActivityIndicator,
    Pressable,
    StyleSheet,
    Switch,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import LottieView from 'lottie-react-native';
import Icon from './SafeIcon';
import DemoModeToggleButton from '../DemoModeToggleButton';
import {IconChip} from '../premium/PremiumKit';
import {BRAND, useManagementTheme} from '../../helpers/managementTheme';
import {useManagementLayout, splitColumns} from '../../helpers/managementLayout';
import {useTopInset} from '../../helpers/safeArea';
import {withHaptic} from '../../helpers/haptics';
import WorkdayHeaderPill from '../workday/WorkdayHeaderPill';

// Safely render values that might be objects (e.g. {first, last})
const renderSafe = (val) => {
    if (val == null) return '';
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
    // Common shape: { first, last }
    if (typeof val === 'object') {
        if ('first' in val || 'last' in val) {
            return `${val.first || ''}${val.first && val.last ? ' ' : ''}${val.last || ''}`.trim();
        }
        // If it's an array, join primitive entries
        if (Array.isArray(val)) return val.map((v) => renderSafe(v)).join(', ');
        // Fallback to JSON for complex objects
        try {
            return JSON.stringify(val);
        } catch (_e) {
            return String(val);
        }
    }
    return String(val);
};

/* -------------------------------------------------------------------------- */
/*  Themed top bar with back button                                            */
/* -------------------------------------------------------------------------- */
export const TopBar = ({title, subtitle, onBack, right}) => {
    const m = useManagementTheme();
    // Pad the bar down below the status bar / notch / Dynamic Island instead of
    // the old hardcoded paddingTop:52 (which clipped under the Island on 14 Pro+).
    const topInset = useTopInset();
    return (
        <View style={[styles.topBar, {paddingTop: topInset + 8}]}>
            {onBack && (
                <TouchableOpacity
                    onPress={onBack}
                    hitSlop={12}
                    activeOpacity={0.7}
                    accessibilityRole="button"
                    style={[styles.topBarBtn, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}
                >
                    <Icon name="chevron-left" size={24} color={m.colors.heading} />
                </TouchableOpacity>
            )}
            <View style={[styles.topBarTitleWrap, {marginLeft: onBack ? 12 : 2}]}>
                <Text style={[styles.topBarTitle, {color: m.colors.heading}]} numberOfLines={1}>{renderSafe(title)}</Text>
                {!!subtitle && <Text style={[styles.topBarSubtitle, {color: m.colors.subText}]} numberOfLines={1}>{renderSafe(subtitle)}</Text>}
            </View>
            {/* Munkanap pill: composed IN FRONT of the screen's own right slot (never replaces it); renders nothing
                while the feature is unsupported / not loaded. */}
            <View style={styles.topBarRight}>
                <WorkdayHeaderPill compact />
                {right ? <View style={styles.topBarRightInner}>{right}</View> : null}
            </View>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Gradient hero header                                                       */
/* -------------------------------------------------------------------------- */
export const GradientHero = ({eyebrow, title, subtitle, icon, scope, badges, children, gradient, onIconPress, secondaryIcon, onSecondaryPress}) => {
    const m = useManagementTheme();
    return (
        <LinearGradient
            colors={gradient || m.gradients.hero}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 1}}
            style={[styles.hero, {shadowColor: BRAND.dark}]}
        >
            <View pointerEvents="none" style={styles.heroBlobA} />
            <View pointerEvents="none" style={styles.heroBlobB} />
            <View style={styles.heroTop}>
                <View style={{flex: 1}}>
                    {!!eyebrow && <Text style={styles.heroEyebrow}>{renderSafe(eyebrow)}</Text>}
                    <Text style={styles.heroTitle}>{renderSafe(title)}</Text>
                    {!!subtitle && <Text style={[styles.heroSubtitle, {color: m.colors.heroSub}]}>{renderSafe(subtitle)}</Text>}
                </View>
                <View style={styles.heroActionStack}>
                    {!!icon && (onIconPress ? (
                        <TouchableOpacity onPress={onIconPress} activeOpacity={0.85} style={styles.heroIcon}>
                            <Icon name={icon} size={24} color="#FFFFFF" />
                        </TouchableOpacity>
                    ) : (
                        <View style={styles.heroIcon}>
                            <Icon name={icon} size={24} color="#FFFFFF" />
                        </View>
                    ))}
                    {!!secondaryIcon && !!onSecondaryPress && (
                        <TouchableOpacity onPress={onSecondaryPress} activeOpacity={0.85} style={[styles.heroIcon, styles.heroIconSecondary]}>
                            <Icon name={secondaryIcon} size={24} color="#FFFFFF" />
                        </TouchableOpacity>
                    )}
                    {/* Top-right Demo (Bemutató) toggle — self-gates to eligible roles. */}
                    <DemoModeToggleButton color="#FFFFFF" style={styles.heroDemoBtn} />
                </View>
            </View>

            {!!scope && (
                <View style={styles.scopePill}>
                    <Icon name={scope.allPlaces ? 'storefront-outline' : 'store-marker-outline'} size={15} color="#FFFFFF" />
                    <Text style={styles.scopeText}>{scope.label}</Text>
                </View>
            )}

            {!!badges?.length && (
                <View style={styles.heroBadges}>
                    {badges.map((b) => (
                        <View key={b.label} style={styles.heroBadge}>
                            {!!b.icon && <Icon name={b.icon} size={13} color="#FFFFFF" />}
                            <Text style={styles.heroBadgeText}>{b.label}</Text>
                        </View>
                    ))}
                </View>
            )}
            {children}
        </LinearGradient>
    );
};

/* -------------------------------------------------------------------------- */
/*  Stat tile with gradient icon                                               */
/* -------------------------------------------------------------------------- */
export const StatTile = ({icon, label, value, tone = 'primary', width = '48%'}) => {
    const m = useManagementTheme();
    return (
        <View style={[styles.statCard, {width, backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1)]}>
            <IconChip icon={icon} tone={tone} size={40} style={styles.statIcon} />
            <Text style={[styles.statValue, {color: m.colors.heading}]} numberOfLines={1}>{renderSafe(value)}</Text>
            <Text style={[styles.statLabel, {color: m.colors.subText}]} numberOfLines={2}>{renderSafe(label)}</Text>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Card / Section                                                             */
/* -------------------------------------------------------------------------- */
export const Card = ({children, style, padded = true}) => {
    const m = useManagementTheme();
    return (
        <View style={[styles.card, padded && styles.cardPadded, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(1), style]}>
            {children}
        </View>
    );
};

/**
 * Section heading used across the management surfaces.
 *
 * Two visual variants share one API so screens can mix them without layout
 * drift:
 *  - default: bold 17px heading with an optional inline icon;
 *  - `eyebrow`: small uppercase letter-spaced label (premium "eyebrow" style)
 *    — pass `tone` to put the icon on a small gradient chip (Modules hub
 *    section headers), otherwise the icon renders inline in the brand color.
 *
 * @param {Object} props - Component props.
 * @param {string} props.title - Heading text.
 * @param {string} [props.actionLabel] - Optional right-aligned action text.
 * @param {Function} [props.onPress] - Handler for the action text.
 * @param {string} [props.icon] - MDI icon rendered before the title.
 * @param {boolean} [props.eyebrow=false] - Render as a small uppercase eyebrow label.
 * @param {string} [props.tone] - Tone key; wraps the icon in a gradient chip (eyebrow variant).
 * @returns {JSX.Element} The section heading row.
 */
export const SectionTitle = ({title, actionLabel, onPress, icon, eyebrow = false, tone}) => {
    const m = useManagementTheme();
    return (
        <View style={styles.sectionHeader}>
            <View style={styles.sectionTitleWrap}>
                {!!icon && (eyebrow || tone ? (
                    <IconChip icon={icon} tone={tone || 'primary'} size={28} round style={styles.sectionIconChip} />
                ) : (
                    <Icon name={icon} size={18} color={m.colors.primary} style={{marginRight: 7}} />
                ))}
                <Text style={[styles.sectionTitle, {color: m.colors.heading}]} numberOfLines={1}>{renderSafe(title)}</Text>
            </View>
            {!!actionLabel && (
                <TouchableOpacity onPress={onPress} hitSlop={8} style={styles.sectionActionWrap}>
                    <Text style={[styles.sectionAction, {color: m.colors.primary}]}>{renderSafe(actionLabel)}</Text>
                    <Icon name="chevron-right" size={16} color={m.colors.primary} />
                </TouchableOpacity>
            )}
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Status badge                                                               */
/* -------------------------------------------------------------------------- */
const TONE_COLORS = {
    primary: {bg: 'rgba(249,115,22,0.14)', fg: '#EA580C'},
    success: {bg: 'rgba(16,185,129,0.16)', fg: '#10B981'},
    danger: {bg: 'rgba(239,68,68,0.16)', fg: '#EF4444'},
    warning: {bg: 'rgba(245,158,11,0.18)', fg: '#D97706'},
    info: {bg: 'rgba(37,99,235,0.16)', fg: '#3B82F6'},
    violet: {bg: 'rgba(124,58,237,0.16)', fg: '#8B5CF6'},
    neutral: {bg: 'rgba(100,116,139,0.16)', fg: '#64748B'},
};

export const StatusBadge = ({label, tone = 'neutral', icon, color}) => {
    const c = /^#[0-9A-Fa-f]{6}$/.test(color || '') ? {bg: `${color}26`, fg: color} : (TONE_COLORS[tone] || TONE_COLORS.neutral);
    return (
        <View style={[styles.badge, {backgroundColor: c.bg}]}>
            {!!icon && <Icon name={icon} size={12} color={c.fg} style={{marginRight: 4}} />}
            <Text style={[styles.badgeText, {color: c.fg}]} numberOfLines={1}>{renderSafe(label)}</Text>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  List row                                                                   */
/* -------------------------------------------------------------------------- */
export const ListRow = ({icon, iconTone = 'primary', title, subtitle, details, meta, badge, chip, actions, onPress, selectable = false, selected = false, onLongPress}) => {
    const m = useManagementTheme();
    return (
        <Pressable
            onPress={onPress}
            onLongPress={onLongPress}
            style={({pressed}) => [
                styles.row,
                {backgroundColor: m.colors.surface, borderColor: selected ? m.colors.primary : m.colors.border, opacity: pressed ? 0.85 : 1},
                m.shadow(1),
            ]}
        >
            {selectable && (
                <Icon name={selected ? 'checkbox-marked' : 'checkbox-blank-outline'} size={22} color={selected ? m.colors.primary : m.colors.muted} style={{marginRight: 10}} />
            )}
            {!!icon && <IconChip icon={icon} tone={iconTone} size={44} style={styles.rowIcon} />}
            <View style={styles.rowMain}>
                <Text style={[styles.rowTitle, {color: m.colors.heading}]} numberOfLines={1}>{renderSafe(title)}</Text>
                {!!subtitle && <Text style={[styles.rowSubtitle, {color: m.colors.subText}]} numberOfLines={2}>{renderSafe(subtitle)}</Text>}
                {!!details?.length && (
                    <View style={styles.rowDetails}>
                        {details.map((detail) => (
                            <View key={`${detail.label}-${detail.value}`} style={styles.rowDetailItem}>
                                <Text style={[styles.rowDetailLabel, {color: m.colors.subText}]} numberOfLines={1}>{detail.label}</Text>
                                <Text style={[styles.rowDetailValue, {color: m.colors.heading}]} numberOfLines={1}>{renderSafe(detail.value)}</Text>
                            </View>
                        ))}
                    </View>
                )}
            </View>
            <View style={styles.rowEnd}>
                {!!chip && <StatusBadge label={chip.label} tone="neutral" color={chip.color} />}
                {!!badge && <StatusBadge label={badge.label} tone={badge.tone} />}
                {!!meta && <Text style={[styles.rowMeta, {color: m.colors.heading}]} numberOfLines={1}>{renderSafe(meta)}</Text>}
                <Icon name="chevron-right" size={20} color={m.colors.muted} />
            </View>
            {!!actions?.length && (
                <View style={styles.rowActions}>
                    {actions.map((action) => (
                        <TouchableOpacity
                            key={action.label}
                            onPress={action.onPress}
                            activeOpacity={0.85}
                            style={[
                                styles.rowAction,
                                {backgroundColor: action.tone === 'danger' ? 'rgba(239,68,68,0.10)' : m.colors.surfaceAlt, borderColor: action.tone === 'danger' ? 'rgba(239,68,68,0.20)' : m.colors.borderSoft},
                            ]}
                        >
                            {!!action.icon && <Icon name={action.icon} size={14} color={action.tone === 'danger' ? '#EF4444' : m.colors.primary} style={{marginRight: 5}} />}
                            <Text style={[styles.rowActionText, {color: action.tone === 'danger' ? '#EF4444' : m.colors.heading}]} numberOfLines={1}>{action.label}</Text>
                        </TouchableOpacity>
                    ))}
                </View>
            )}
        </Pressable>
    );
};

/* -------------------------------------------------------------------------- */
/*  Module / navigation card                                                   */
/* -------------------------------------------------------------------------- */
/**
 * Premium navigation tile — gradient icon chip, bold title, muted subtitle and
 * a soft circular chevron affordance. Pressing scales the card down slightly
 * for tactile feedback; a locked tile renders greyed-out with its reason.
 *
 * @param {Object} props - Component props.
 * @param {string} props.icon - MDI icon on the gradient chip.
 * @param {string} [props.tone='info'] - Gradient tone of the icon chip.
 * @param {string} props.title - Bold tile title.
 * @param {string} [props.subtitle] - Muted one-line description.
 * @param {Function} [props.onPress] - Tap handler (ignored when disabled).
 * @param {boolean} [props.disabled=false] - Render the locked state.
 * @param {string} [props.lockedReason] - Why the tile is locked (shown under the subtitle).
 * @returns {JSX.Element} The module tile.
 */
export const ModuleCard = ({icon, tone = 'primary', title, subtitle, onPress, disabled = false, lockedReason}) => {
    const m = useManagementTheme();
    return (
        <Pressable
            onPress={disabled ? undefined : onPress}
            disabled={disabled}
            style={({pressed}) => [
                styles.moduleCard,
                {backgroundColor: m.colors.surface, borderColor: m.colors.border, opacity: disabled ? 0.52 : (pressed ? 0.9 : 1)},
                !disabled && pressed && {transform: [{scale: 0.98}]},
                m.shadow(1),
            ]}
        >
            <IconChip icon={disabled ? 'lock-outline' : icon} tone={disabled ? 'neutral' : tone} size={52} style={styles.moduleIcon} />
            <View style={styles.moduleText}>
                <Text style={[styles.moduleTitle, {color: m.colors.heading}]}>{renderSafe(title)}</Text>
                {!!subtitle && <Text style={[styles.moduleSubtitle, {color: m.colors.subText}]} numberOfLines={2}>{renderSafe(subtitle)}</Text>}
                {disabled && !!lockedReason && <Text style={[styles.moduleLocked, {color: m.colors.muted}]}>{renderSafe(lockedReason)}</Text>}
            </View>
            <View style={[styles.moduleChevron, {backgroundColor: m.colors.surfaceAlt}]}>
                <Icon name={disabled ? 'lock-outline' : 'chevron-right'} size={18} color={m.colors.muted} />
            </View>
        </Pressable>
    );
};

/* -------------------------------------------------------------------------- */
/*  Quick action chip (horizontal shortcut row)                                */
/* -------------------------------------------------------------------------- */
/**
 * Compact horizontal shortcut pill — gradient icon chip + bold label +
 * chevron — for the dashboard "Quick actions" row. Same tactile press
 * feedback as {@link ModuleCard}, sized to sit several-per-row inside a
 * horizontal ScrollView.
 *
 * @param {Object} props - Component props.
 * @param {string} props.icon - MDI icon on the gradient chip.
 * @param {string} props.label - One-line action label.
 * @param {string} [props.tone='primary'] - Gradient tone of the icon chip.
 * @param {Function} [props.onPress] - Tap handler.
 * @returns {JSX.Element} The quick-action pill.
 */
export const QuickActionCard = ({icon, label, tone = 'primary', onPress}) => {
    const m = useManagementTheme();
    return (
        <Pressable
            onPress={onPress}
            style={({pressed}) => [
                styles.quickAction,
                {backgroundColor: m.colors.surface, borderColor: m.colors.border, opacity: pressed ? 0.9 : 1},
                pressed && {transform: [{scale: 0.97}]},
                m.shadow(1),
            ]}
        >
            <IconChip icon={icon} tone={tone} size={36} round style={styles.quickActionIcon} />
            <Text style={[styles.quickActionLabel, {color: m.colors.heading}]} numberOfLines={1}>{renderSafe(label)}</Text>
            <Icon name="chevron-right" size={16} color={m.colors.muted} />
        </Pressable>
    );
};

/* -------------------------------------------------------------------------- */
/*  Responsive two-column grid                                                 */
/* -------------------------------------------------------------------------- */
/**
 * Responsive column wrapper that arranges children two-per-row on tablets.
 *
 * NOTE: reserved for small, uniform tiles (e.g. stat/metric tiles) — NOT for
 * placing two *different navigation modules* side by side, which reads as two
 * unrelated screens crammed together. For making a long single record read
 * without endless scrolling, use {@link DetailColumns} (block-level) or
 * {@link FieldGrid} / {@link FieldCell} (field-level) instead.
 *
 * On tablets / wide screens (>= 768dp, see {@link useManagementLayout}) the
 * children are arranged two-per-row so the extra horizontal space is actually
 * used; on phones it degrades transparently to the normal single stacked
 * column. Each child keeps its own vertical margin. Falsy children
 * (conditionally-rendered cards) are dropped.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Full-width cards/rows to lay out.
 * @param {Object|Object[]} [props.style] - Extra style for the wrapping row.
 * @returns {JSX.Element} The single- or two-column layout.
 */
export const ManagementGrid = ({children, style}) => {
    const {columns, gap} = useManagementLayout();
    const items = React.Children.toArray(children);
    // Phone (or a single card) → render the children untouched as one column.
    if (columns <= 1 || items.length < 2) {
        return <>{children}</>;
    }

    return (
        <View style={[styles.grid, {columnGap: gap}, style]}>
            {items.map((child, index) => (
                <View key={child.key ?? index} style={styles.gridCell}>{child}</View>
            ))}
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Detail-page two-column layout (reduce scrolling on tablets)                */
/* -------------------------------------------------------------------------- */
/**
 * Block-level two-column layout for long detail / config pages.
 *
 * Unlike {@link ManagementGrid} (which pairs cards two-per-row and keeps the
 * grid growing downward), this splits a tall vertical stack of *independent*
 * blocks (info cards, section groups, …) into two side-by-side columns on
 * tablets — so a long record reads across the screen instead of scrolling
 * forever. On phones it transparently renders the children in their original
 * order as a single column. Put genuinely full-width elements (page hero,
 * sticky action/save bar, tab bar) OUTSIDE this wrapper.
 *
 * Blocks are distributed round-robin (see {@link splitColumns}); each block
 * keeps its own vertical margin, so existing cards drop in unchanged.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Independent blocks to columnize.
 * @param {Object|Object[]} [props.style] - Extra style for the wrapping row.
 * @returns {JSX.Element} The single- or two-column layout.
 */
export const DetailColumns = ({children, style}) => {
    const {isWide, gap} = useManagementLayout();
    const items = React.Children.toArray(children).filter(Boolean);
    // Phone (or a single block) → keep the natural single-column flow.
    if (!isWide || items.length < 2) {
        return <>{children}</>;
    }

    const columns = splitColumns(items, 2);

    return (
        <View style={[styles.detailColumns, {columnGap: gap}, style]}>
            {columns.map((col, index) => (
                <View key={`detail-col-${index}`} style={styles.detailColumn}>{col}</View>
            ))}
        </View>
    );
};

/**
 * Field-level responsive grid — lays short form fields two-up on tablets.
 *
 * Wrap a card's field stack in `FieldGrid` and each field in a {@link FieldCell}
 * to halve the vertical height of a tall form on tablets while keeping natural
 * left-to-right, top-to-bottom reading order. On phones both components are
 * pass-throughs, so fields stack exactly as before. A {@link FieldCell} marked
 * `full` (multi-line inputs, localized groups, switches) spans the whole row.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - {@link FieldCell}-wrapped fields.
 * @param {Object|Object[]} [props.style] - Extra style for the wrapping row.
 * @returns {JSX.Element} The single- or two-column field layout.
 */
export const FieldGrid = ({children, style}) => {
    const {isWide, gap} = useManagementLayout();
    if (!isWide) {
        return <>{children}</>;
    }

    return <View style={[styles.fieldGrid, {columnGap: gap}, style]}>{children}</View>;
};

/**
 * A single cell inside a {@link FieldGrid}.
 *
 * On tablets a normal cell takes roughly half the row (two per line); a `full`
 * cell spans the entire row (use for multi-line inputs, localized language
 * groups, switches and anything that needs the full width to stay legible). On
 * phones it renders its child untouched.
 *
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - The field element to place.
 * @param {boolean} [props.full=false] - Force the cell to span the full row.
 * @returns {JSX.Element} The wrapped (or pass-through) field cell.
 */
export const FieldCell = ({children, full = false}) => {
    const {isWide} = useManagementLayout();
    if (!isWide) {
        return <>{children}</>;
    }

    return <View style={full ? styles.fieldCellFull : styles.fieldCell}>{children}</View>;
};

/* -------------------------------------------------------------------------- */
/*  Metric row inside a card                                                   */
/* -------------------------------------------------------------------------- */
export const MetricRow = ({label, value, tone, last}) => {
    const m = useManagementTheme();
    return (
        <View style={[styles.metricRow, !last && {borderBottomWidth: 1, borderBottomColor: m.colors.borderSoft}]}>
            <Text style={[styles.metricLabel, {color: m.colors.subText}]}>{renderSafe(label)}</Text>
            <Text style={[styles.metricValue, {color: tone || m.colors.heading}]}>{renderSafe(value)}</Text>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Editable field                                                             */
/* -------------------------------------------------------------------------- */
export const EditableField = ({label, value, onChangeText, placeholder, keyboardType, autoCapitalize, multiline, editable = true, icon, suffix, onSubmitEditing, returnKeyType, blurOnSubmit}) => {
    const m = useManagementTheme();
    const [focused, setFocused] = React.useState(false);
    return (
        <View style={styles.fieldWrap}>
            <Text style={[styles.fieldLabel, {color: m.colors.subText}]}>{renderSafe(label)}</Text>
            <View
                style={[
                    styles.fieldInputWrap,
                    {
                        backgroundColor: editable ? m.colors.inputBg : m.colors.surfaceAlt,
                        borderColor: focused ? m.colors.primary : m.colors.inputBorder,
                    },
                    multiline && {alignItems: 'flex-start', minHeight: 90},
                ]}
            >
                {!!icon && <Icon name={icon} size={17} color={m.colors.muted} style={{marginRight: 8, marginTop: multiline ? 2 : 0}} />}
                <TextInput
                    value={value == null ? '' : String(value)}
                    onChangeText={onChangeText}
                    editable={editable}
                    placeholder={placeholder}
                    placeholderTextColor={m.colors.placeholder}
                    keyboardType={keyboardType}
                    autoCapitalize={autoCapitalize}
                    multiline={multiline}
                    onSubmitEditing={onSubmitEditing}
                    returnKeyType={returnKeyType}
                    blurOnSubmit={blurOnSubmit}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    style={[styles.fieldInput, {color: editable ? m.colors.heading : m.colors.subText}]}
                />
                {!!suffix && <Text style={[styles.fieldSuffix, {color: m.colors.muted}]}>{renderSafe(suffix)}</Text>}
            </View>
        </View>
    );
};

export const SwitchField = ({label, description, value, onValueChange, disabled = false}) => {
    const m = useManagementTheme();
    return (
        <View style={[styles.switchRow, {borderColor: m.colors.borderSoft}]}>
            <View style={{flex: 1, paddingRight: 12}}>
                <Text style={[styles.switchLabel, {color: m.colors.heading}]}>{renderSafe(label)}</Text>
                {!!description && <Text style={[styles.switchDesc, {color: m.colors.subText}]}>{renderSafe(description)}</Text>}
            </View>
            <Switch
                value={!!value}
                onValueChange={onValueChange}
                disabled={disabled}
                trackColor={{false: m.colors.border, true: m.colors.primary}}
                thumbColor="#FFFFFF"
            />
        </View>
    );
};

export const ChipSelectField = ({label, value, options, onSelect}) => {
    const m = useManagementTheme();
    return (
        <View style={styles.fieldWrap}>
            <Text style={[styles.fieldLabel, {color: m.colors.subText}]}>{label}</Text>
            <View style={styles.chipRow}>
                {options.map((opt) => {
                    const active = String(opt.value) === String(value);
                    const tone = TONE_COLORS[opt.tone] || TONE_COLORS.primary;
                    return (
                        <TouchableOpacity
                            key={String(opt.value)}
                            onPress={() => onSelect(opt.value)}
                            activeOpacity={0.8}
                            style={[
                                styles.chip,
                                {borderColor: active ? tone.fg : m.colors.border, backgroundColor: active ? tone.bg : 'transparent'},
                            ]}
                        >
                                            <Text style={[styles.chipText, {color: active ? tone.fg : m.colors.subText}]}>{renderSafe(opt.label)}</Text>
                        </TouchableOpacity>
                    );
                })}
            </View>
        </View>
    );
};

/**
 * Multi-select variant of {@link ChipSelectField} — every option whose value is
 * present in `value` (an array) renders active; tapping a chip toggles its
 * membership instead of replacing the whole selection. Used by the lead
 * "Csoportok" (groups) picker.
 *
 * @param {object} props
 * @param {string} props.label field label.
 * @param {Array<string>} props.value currently selected option values.
 * @param {Array<{value: string, label: string, tone?: string}>} props.options
 *   selectable options.
 * @param {(value: string) => void} props.onToggle called with the tapped
 *   option's value; the caller owns adding/removing it from `value`.
 * @returns {JSX.Element}
 */
export const MultiChipSelectField = ({label, value, options, onToggle}) => {
    const m = useManagementTheme();
    const selected = Array.isArray(value) ? value.map(String) : [];
    return (
        <View style={styles.fieldWrap}>
            <Text style={[styles.fieldLabel, {color: m.colors.subText}]}>{label}</Text>
            <View style={styles.chipRow}>
                {options.map((opt) => {
                    const active = selected.includes(String(opt.value));
                    const tone = TONE_COLORS[opt.tone] || TONE_COLORS.primary;
                    return (
                        <TouchableOpacity
                            key={String(opt.value)}
                            onPress={() => onToggle(opt.value)}
                            activeOpacity={0.8}
                            style={[
                                styles.chip,
                                {borderColor: active ? tone.fg : m.colors.border, backgroundColor: active ? tone.bg : 'transparent'},
                            ]}
                        >
                            {active && <Icon name="check" size={13} color={tone.fg} style={{marginRight: 4}} />}
                            <Text style={[styles.chipText, {color: active ? tone.fg : m.colors.subText}]}>{renderSafe(opt.label)}</Text>
                        </TouchableOpacity>
                    );
                })}
            </View>
        </View>
    );
};

/* -------------------------------------------------------------------------- */
/*  Buttons                                                                     */
/* -------------------------------------------------------------------------- */
export const PrimaryButton = ({label, icon, onPress, loading, disabled, tone = 'primary', variant = 'solid'}) => {
    const m = useManagementTheme();
    const grad = m.toneGradient(tone);
    if (variant === 'outline') {
        const c = (TONE_COLORS[tone] || TONE_COLORS.primary);
        return (
            <TouchableOpacity onPress={withHaptic(onPress, 'tap')} disabled={disabled || loading} activeOpacity={0.8} style={[styles.btnOutline, {borderColor: c.fg, opacity: disabled ? 0.5 : 1}]}>
                {loading ? <ActivityIndicator color={c.fg} /> : (
                    <>
                        {!!icon && <Icon name={icon} size={18} color={c.fg} style={{marginRight: 8}} />}
                        <Text style={[styles.btnOutlineText, {color: c.fg}]}>{renderSafe(label)}</Text>
                    </>
                )}
            </TouchableOpacity>
        );
    }
    return (
        <TouchableOpacity onPress={withHaptic(onPress, 'tap')} disabled={disabled || loading} activeOpacity={0.85} style={{flex: 1, opacity: disabled ? 0.5 : 1}}>
            <LinearGradient colors={grad} start={{x: 0, y: 0}} end={{x: 1, y: 0}} style={[styles.btn, tone === 'primary' ? styles.btnGlow : m.shadow(1)]}>
                {loading ? <ActivityIndicator color="#FFFFFF" /> : (
                    <>
                        {!!icon && <Icon name={icon} size={18} color="#FFFFFF" style={{marginRight: 8}} />}
                        <Text style={styles.btnText}>{renderSafe(label)}</Text>
                    </>
                )}
            </LinearGradient>
        </TouchableOpacity>
    );
};

/* -------------------------------------------------------------------------- */
/*  Empty + Error states                                                       */
/* -------------------------------------------------------------------------- */
/**
 * Premium empty-state block used across the management UI.
 *
 * Renders either a Lottie animation (when `animation` is supplied) or the
 * classic gradient-icon tile, followed by a title, optional helper text and an
 * optional call-to-action.
 *
 * @param {Object} props - Component props.
 * @param {string} [props.icon='tray-remove'] - MDI icon name for the gradient tile (ignored when `animation` is set).
 * @param {*} [props.animation] - A `require()`'d Lottie JSON source. When provided, a ~120x120 centered
 *                                animation replaces the gradient-icon tile.
 * @param {string} props.title - Bold heading text.
 * @param {string} [props.text] - Optional supporting paragraph.
 * @param {string} [props.tone='primary'] - Tone key driving the gradient tile colours.
 * @param {React.ReactNode} [props.action] - Optional action element (e.g. a button) rendered below the text.
 * @returns {JSX.Element} The rendered empty-state.
 */
export const EmptyState = ({icon = 'tray-remove', animation, title, text, tone = 'primary', action}) => {
    const m = useManagementTheme();
    return (
        <View style={[styles.empty, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
            {animation ? (
                <LottieView source={animation} autoPlay loop style={styles.emptyAnim} />
            ) : (
                <View style={[styles.emptyRing, {backgroundColor: m.toneTint(tone, m.isDark ? 0.12 : 0.08)}]}>
                    <IconChip icon={icon} tone={tone} size={60} round />
                </View>
            )}
            <Text style={[styles.emptyTitle, {color: m.colors.heading}]}>{title}</Text>
            {!!text && <Text style={[styles.emptyText, {color: m.colors.subText}]}>{text}</Text>}
            {!!action && <View style={{marginTop: 16, alignSelf: 'stretch'}}>{action}</View>}
        </View>
    );
};

export const InlineError = ({message}) => {
    if (!message) return null;
    return (
        <View style={styles.errorBox}>
            <Icon name="alert-circle-outline" size={18} color="#DC2626" style={{marginRight: 8}} />
            <Text style={styles.errorText}>{message}</Text>
        </View>
    );
};

export const NoteBox = ({icon = 'information-outline', children, tone = 'info'}) => {
    const m = useManagementTheme();
    const c = TONE_COLORS[tone] || TONE_COLORS.info;
    return (
        <View style={[styles.noteBox, {backgroundColor: c.bg}]}>
            <Icon name={icon} size={18} color={c.fg} style={{marginRight: 8, marginTop: 1}} />
            <Text style={[styles.noteText, {color: m.isDark ? m.colors.text : c.fg}]}>{children}</Text>
        </View>
    );
};

/**
 * Floating action button — the app-wide premium "add" affordance. Pinned bottom-right
 * (thumb-reachable) with the brand gradient, mirroring the shipped mail ComposeFab. Render
 * it via ManagementScreen's `floating` prop so it stays fixed while the content scrolls.
 *
 * @param {{icon?: string, label?: string, onPress?: Function, tone?: string}} props
 */
export const Fab = ({icon = 'plus', label, onPress, tone = 'primary'}) => {
    const m = useManagementTheme();
    return (
        <TouchableOpacity onPress={onPress} activeOpacity={0.85} style={styles.fabWrap}>
            <LinearGradient
                colors={m.gradients[tone] || m.gradients.primary}
                start={{x: 0, y: 0}}
                end={{x: 1, y: 1}}
                style={[styles.fab, styles.fabGlow]}
            >
                <Icon name={icon} size={22} color="#FFFFFF" />
                {!!label && <Text style={styles.fabText}>{label}</Text>}
            </LinearGradient>
        </TouchableOpacity>
    );
};

/* -------------------------------------------------------------------------- */
const styles = StyleSheet.create({
    topBar: {flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingBottom: 12},
    topBarBtn: {width: 42, height: 42, borderRadius: 21, borderWidth: 1, alignItems: 'center', justifyContent: 'center'},
    topBarTitleWrap: {flex: 1, justifyContent: 'center'},
    topBarTitle: {fontSize: 22, fontWeight: '800', letterSpacing: -0.4},
    topBarSubtitle: {fontSize: 12, fontWeight: '600', marginTop: 2},
    topBarRight: {marginLeft: 6, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8},
    topBarRightInner: {alignItems: 'flex-end', justifyContent: 'center'},

    hero: {borderRadius: 28, padding: 22, marginBottom: 18, overflow: 'hidden', shadowOffset: {width: 0, height: 14}, shadowOpacity: 0.26, shadowRadius: 22, elevation: 8},
    heroBlobA: {position: 'absolute', width: 220, height: 220, borderRadius: 110, backgroundColor: 'rgba(255,255,255,0.12)', top: -90, right: -70},
    heroBlobB: {position: 'absolute', width: 160, height: 160, borderRadius: 80, backgroundColor: 'rgba(255,255,255,0.09)', bottom: -70, left: -50},
    heroTop: {flexDirection: 'row', alignItems: 'flex-start'},
    heroEyebrow: {color: 'rgba(255,255,255,0.9)', fontSize: 12, fontWeight: '800', letterSpacing: 0.4},
    heroTitle: {color: '#FFFFFF', fontSize: 26, lineHeight: 32, fontWeight: '800', letterSpacing: -0.5, marginTop: 6},
    heroSubtitle: {fontSize: 13.5, lineHeight: 20, marginTop: 8},
    heroIcon: {width: 46, height: 46, borderRadius: 23, backgroundColor: 'rgba(255,255,255,0.22)', alignItems: 'center', justifyContent: 'center', marginLeft: 12},
    heroActionStack: {alignItems: 'flex-end'},
    heroIconSecondary: {marginTop: 10},
    heroDemoBtn: {marginTop: 10, marginLeft: 12, backgroundColor: 'rgba(255,255,255,0.22)'},
    scopePill: {alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: 'rgba(255,255,255,0.22)', borderRadius: 999, paddingHorizontal: 13, paddingVertical: 7, marginTop: 16},
    scopeText: {color: '#FFFFFF', fontWeight: '800', fontSize: 13},
    heroBadges: {flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14},
    heroBadge: {flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.18)', borderRadius: 999, paddingHorizontal: 11, paddingVertical: 6},
    heroBadgeText: {color: '#FFFFFF', fontSize: 12, fontWeight: '700'},

    statCard: {minHeight: 118, borderRadius: 24, padding: 16, borderWidth: 1},
    statIcon: {marginBottom: 12},
    statValue: {fontSize: 22, fontWeight: '800', letterSpacing: -0.4},
    statLabel: {fontSize: 12, fontWeight: '600', marginTop: 3},

    card: {borderRadius: 24, borderWidth: 1},
    cardPadded: {padding: 16},

    sectionHeader: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 22, marginBottom: 12},
    sectionTitleWrap: {flexDirection: 'row', alignItems: 'center', flex: 1},
    sectionTitle: {fontSize: 18, fontWeight: '800', letterSpacing: -0.2, flexShrink: 1},
    sectionEyebrow: {fontSize: 12, fontWeight: '900', textTransform: 'uppercase', letterSpacing: 1.1},
    sectionIconChip: {marginRight: 8},
    sectionActionWrap: {flexDirection: 'row', alignItems: 'center'},
    sectionAction: {fontSize: 13.5, fontWeight: '700'},

    badge: {flexDirection: 'row', alignItems: 'center', borderRadius: 999, paddingHorizontal: 10, paddingVertical: 4},
    badgeText: {fontSize: 11.5, fontWeight: '800'},

    row: {flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', borderRadius: 22, paddingHorizontal: 13, paddingVertical: 12, borderWidth: 1, marginBottom: 10},
    rowIcon: {marginRight: 11},
    rowMain: {flex: 1, minWidth: 0, paddingRight: 8},
    rowTitle: {fontSize: 15, fontWeight: '700'},
    rowSubtitle: {fontSize: 12.5, marginTop: 2, lineHeight: 16.5},
    rowDetails: {flexDirection: 'row', flexWrap: 'wrap', columnGap: 8, rowGap: 6, marginTop: 8},
    rowDetailItem: {minWidth: 72, flexBasis: '31%', flexGrow: 1},
    rowDetailLabel: {fontSize: 10, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.3},
    rowDetailValue: {fontSize: 12, fontWeight: '700', marginTop: 2, lineHeight: 15},
    rowEnd: {alignItems: 'center', flexDirection: 'row', gap: 6, flexShrink: 1, maxWidth: '40%'},
    rowMeta: {fontSize: 12.5, fontWeight: '800', flexShrink: 1},
    rowActions: {width: '100%', flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 10},
    rowAction: {flexDirection: 'row', alignItems: 'center', borderRadius: 999, borderWidth: 1, paddingHorizontal: 10, paddingVertical: 6},
    rowActionText: {fontSize: 11, fontWeight: '800'},

    moduleCard: {flexDirection: 'row', alignItems: 'center', borderRadius: 24, padding: 14, marginBottom: 12, borderWidth: 1},
    moduleIcon: {marginRight: 14},
    moduleText: {flex: 1},
    moduleTitle: {fontSize: 16, fontWeight: '700', letterSpacing: -0.1},
    moduleSubtitle: {fontSize: 12.5, marginTop: 3, lineHeight: 17},
    moduleLocked: {fontSize: 11.5, marginTop: 6, lineHeight: 16, fontWeight: '800'},
    moduleChevron: {width: 30, height: 30, borderRadius: 999, alignItems: 'center', justifyContent: 'center', marginLeft: 8},

    // Quick-action pill (see QuickActionCard) — compact shortcut for the
    // dashboard's horizontal "Quick actions" row.
    quickAction: {flexDirection: 'row', alignItems: 'center', gap: 9, borderRadius: 999, borderWidth: 1, paddingVertical: 8, paddingLeft: 8, paddingRight: 14, marginRight: 10},
    quickActionIcon: {},
    quickActionLabel: {fontSize: 13.5, fontWeight: '800'},

    // Two-column tablet grid (see ManagementGrid). flexBasis 47% + flexGrow lets
    // exactly two cards share a row and fill it; a third can't fit so it wraps.
    grid: {flexDirection: 'row', flexWrap: 'wrap', alignItems: 'stretch'},
    gridCell: {flexGrow: 1, flexBasis: '47%', minWidth: 240},

    // Detail-page two-column layout (see DetailColumns). Two equal columns, each
    // an independent vertical stack; alignItems flex-start so a short column
    // doesn't stretch to match the taller one.
    detailColumns: {flexDirection: 'row', alignItems: 'flex-start'},
    detailColumn: {flex: 1, minWidth: 0},

    // Field-level two-up grid (see FieldGrid / FieldCell). flexBasis 48% + grow
    // packs two short fields per row; a `full` cell (100%) forces its own line.
    fieldGrid: {flexDirection: 'row', flexWrap: 'wrap'},
    fieldCell: {flexBasis: '48%', flexGrow: 1, minWidth: 240},
    fieldCellFull: {flexBasis: '100%', width: '100%'},

    metricRow: {flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 12},
    metricLabel: {fontSize: 13.5, fontWeight: '600'},
    metricValue: {fontSize: 14.5, fontWeight: '800'},

    fieldWrap: {marginBottom: 14},
    fieldLabel: {fontSize: 12, fontWeight: '800', marginBottom: 7, textTransform: 'uppercase', letterSpacing: 0.4},
    fieldInputWrap: {flexDirection: 'row', alignItems: 'center', borderRadius: 16, borderWidth: 1.2, paddingHorizontal: 14, paddingVertical: 12},
    fieldInput: {flex: 1, fontSize: 15, fontWeight: '600', padding: 0, margin: 0},
    fieldSuffix: {fontSize: 13, fontWeight: '700', marginLeft: 8},

    switchRow: {flexDirection: 'row', alignItems: 'center', paddingVertical: 13, borderBottomWidth: 1},
    switchLabel: {fontSize: 14.5, fontWeight: '800'},
    switchDesc: {fontSize: 12.5, marginTop: 3, lineHeight: 17},

    chipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 8},
    chip: {borderRadius: 999, borderWidth: 1.4, paddingHorizontal: 14, paddingVertical: 8},
    chipText: {fontSize: 13, fontWeight: '800'},

    btn: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', borderRadius: 999, paddingVertical: 15},
    btnGlow: {shadowColor: BRAND.dark, shadowOffset: {width: 0, height: 8}, shadowOpacity: 0.3, shadowRadius: 14, elevation: 5},
    btnText: {color: '#FFFFFF', fontSize: 15.5, fontWeight: '800', letterSpacing: 0.1},
    btnOutline: {flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', borderRadius: 999, paddingVertical: 14, borderWidth: 1.6},
    btnOutlineText: {fontSize: 15, fontWeight: '900'},

    empty: {borderRadius: 24, padding: 28, alignItems: 'center', borderWidth: 1},
    emptyAnim: {width: 120, height: 120, marginBottom: 6},
    emptyRing: {width: 92, height: 92, borderRadius: 46, alignItems: 'center', justifyContent: 'center', marginBottom: 14},
    emptyTitle: {fontSize: 16, fontWeight: '900'},
    emptyText: {fontSize: 13, textAlign: 'center', marginTop: 6, lineHeight: 19},

    errorBox: {flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(239,68,68,0.14)', borderRadius: 14, padding: 13, marginBottom: 14},
    errorText: {flex: 1, color: '#DC2626', fontSize: 13.5, fontWeight: '700'},

    noteBox: {flexDirection: 'row', alignItems: 'flex-start', borderRadius: 16, padding: 14, marginTop: 6},
    noteText: {flex: 1, fontSize: 13, lineHeight: 19, fontWeight: '600'},
    fabWrap: {position: 'absolute', right: 18, bottom: 26},
    fab: {flexDirection: 'row', alignItems: 'center', justifyContent: 'center', height: 58, minWidth: 58, borderRadius: 29, paddingHorizontal: 20},
    fabGlow: {shadowColor: BRAND.dark, shadowOffset: {width: 0, height: 10}, shadowOpacity: 0.36, shadowRadius: 16, elevation: 8},
    fabText: {color: '#FFFFFF', fontSize: 15, fontWeight: '800', marginLeft: 8},
});

export const managementUiStyles = styles;




